import {Injectable} from '@angular/core';
import {mergeMap, Observable, switchMap, withLatestFrom} from 'rxjs';
import {TypedAction} from '@ngrx/store/src/models';
import {Serializer, SerializerResponse, Serializers} from '../../interfaces/serializer';
import {DataSetDimension} from '../../interfaces/dimensions';
import {Actions, createEffect, CreateEffectMetadata, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {AppState} from '../store';
import {
  loadDataSetDimensions,
  loadDataSetDimensionsSuccess,
  loadDataSetUsageDimensions,
  loadPublicDataSetDimensions,
  loadPublicDataSetDimensionsSuccess,
  updateDataSetDimension,
  updateDataSetDimensions,
  updateDataSetDimensionsSuccess,
  updateDataSetDimensionSuccess
} from './dataSetDimensions.actions';
import {DimensionsService} from '../../services/dimensions.service';
import {selectSite} from '../init/init.selectors';
import {Site} from '../../interfaces/site';
import {filter, map, tap} from 'rxjs/operators';
import {PublicReportsService} from '../../services/public-reports.service';
import {DatasetUsageComponentObject} from "../../classes/dataset-usage-component-object";

@Injectable()
export class DataSetDimensionsEffects {
  private readonly _dataset$: Observable<Serializer<Site>> = this._store.select(selectSite);

  private readonly _load$: Observable<TypedAction<string> & { dimensions: Serializers<DataSetDimension> }> & CreateEffectMetadata = createEffect((): Observable<TypedAction<string> & { dimensions: Serializers<DataSetDimension> }> => this._actions$.pipe(
    ofType(loadDataSetDimensions),
    withLatestFrom(this._dataset$),
    switchMap(([_, dataset]: [TypedAction<string>, Serializer<Site>]): Observable<SerializerResponse<Serializers<DataSetDimension>>> => this._dimensionsService.getDataSetDimensions(dataset)),
    map((response: SerializerResponse<Serializers<DataSetDimension>>): TypedAction<string> & { dimensions: Serializers<DataSetDimension> } => loadDataSetDimensionsSuccess({
      dimensions: response.data
    }))
  ));

  private readonly _updates$: Observable<TypedAction<string> & { dimensions: Serializers<DataSetDimension> }> & CreateEffectMetadata = createEffect((): Observable<TypedAction<string> & { dimensions: Serializers<DataSetDimension> }> => this._actions$.pipe(
    ofType(updateDataSetDimensions),
    withLatestFrom(this._dataset$),
    switchMap(([_, dataset]: [TypedAction<string>, Serializer<Site>]): Observable<SerializerResponse<Serializers<DataSetDimension>>> => this._dimensionsService.getDataSetDimensions(dataset)),
    map((response: SerializerResponse<Serializers<DataSetDimension>>): TypedAction<string> & { dimensions: Serializers<DataSetDimension> } => updateDataSetDimensionsSuccess({
      dimensions: response.data
    }))
  ));

  private readonly _loadPublic$: Observable<TypedAction<string> & { dimensions: Serializers<DataSetDimension> }> & CreateEffectMetadata = createEffect((): Observable<TypedAction<string> & { dimensions: Serializers<DataSetDimension> }> => this._actions$.pipe(
    ofType(loadPublicDataSetDimensions),
    switchMap((action: TypedAction<string> & { uuid: string, token: string }): Observable<SerializerResponse<Serializers<DataSetDimension>>> => this._publicReportsService.getDataSetDimensions(action.uuid, action.token)),
    map((response: SerializerResponse<Serializers<DataSetDimension>>): TypedAction<string> & { dimensions: Serializers<DataSetDimension> } => loadPublicDataSetDimensionsSuccess({
      dimensions: response.data
    }))
  ));

  private readonly _update$: Observable<TypedAction<string> & { dimension: Serializer<DataSetDimension> }> & CreateEffectMetadata = createEffect((): Observable<TypedAction<string> & { dimension: Serializer<DataSetDimension> }> => this._actions$.pipe(
    ofType(updateDataSetDimension),
    withLatestFrom(this._dataset$),
    mergeMap(([action, dataset]: [TypedAction<string> & { dimension: Serializer<DataSetDimension>, update: Partial<DataSetDimension> }, Serializer<Site>]): Observable<SerializerResponse<Serializer<DataSetDimension>>> => this._dimensionsService.updateDataSetDimension(dataset, action.dimension, action.update)),
    map((response: SerializerResponse<Serializer<DataSetDimension>>): TypedAction<string> & { dimension: Serializer<DataSetDimension> } => updateDataSetDimensionSuccess({
      dimension: response.data
    }))
  ));

  private readonly _loadUsageDimensions$: Observable<TypedAction<string>> & CreateEffectMetadata = createEffect(() => this._actions$.pipe(
    ofType(loadDataSetUsageDimensions),
    filter((action: TypedAction<string> & { dataSetUsage: DatasetUsageComponentObject }): boolean => !action.dataSetUsage.dimensionsLoaded$.value),
    tap((action: TypedAction<string> & { dataSetUsage: DatasetUsageComponentObject }) => action.dataSetUsage.dimensionsLoaded$.next(true)),
    map((): TypedAction<string> => loadDataSetDimensions())
  ));

  constructor(
    private readonly _actions$: Actions,
    private readonly _store: Store<AppState>,
    private readonly _dimensionsService: DimensionsService,
    private readonly _publicReportsService: PublicReportsService
  ) {}

}
